// De editor: TipTap met precies de blokken die O1 belooft — koppen, lijsten, gemengde
// checklists, codeblokken en afbeeldingen die je erin plakt.

import { useEffect, useRef, useState } from "react";
import { EditorContent, useEditor, type Editor as TipTapEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { Placeholder } from "@tiptap/extensions";
import Image from "@tiptap/extension-image";
import { TableKit } from "@tiptap/extension-table";
import type { Blok } from "../api.ts";
import { Icoon, type IcoonNaam } from "./Icoon.tsx";
import { NotitieLink } from "./NotitieLink.ts";
import { verkleinAlsNodig, GROOTSTE_BESTAND } from "./afbeelding.ts";
import { Bijlage } from "./Bijlage.ts";
import { Callout, CALLOUT_SOORTEN } from "./Callout.ts";
import { Codeblok } from "./Codeblok.ts";
import { Vouwen } from "./Vouwen.ts";
import { Blokgreep, useZwevendBlok } from "./Blokmenu.tsx";
// Rechtstreeks uit de serverkant: markdown lezen en schrijven is precies hetzelfde werk aan
// beide kanten, en twee versies zouden onvermijdelijk uit elkaar gaan lopen. Allebei de
// bestanden zijn pure TypeScript zonder Node erin, dus Vite bundelt ze gewoon mee.
import { naarMarkdown } from "../../../server/kern/inhoud.ts";
import { vanMarkdown } from "../../../server/kern/markdown.ts";

const LEEG: Blok = { type: "doc", content: [{ type: "paragraph" }] };

/**
 * Een bestand wegschrijven: een foto, of sinds O3 ook een pdf, een zip, wat dan ook. Geeft
 * een reden terug in plaats van stilletjes `null`: de eerste keer dat er op de telefoon een
 * foto in een notitie moest, weigerde de server hem (te groot) en gebeurde er op het scherm
 * helemaal niets — geen afbeelding, geen uitleg.
 */
type Weggeschreven = { url: string; afbeelding: boolean; naam: string; grootte: number; mime: string };

async function stuurBestand(bestand: File): Promise<Weggeschreven | { fout: string }> {
  // Alleen afbeeldingen gaan door de verkleiner; een pdf of een zip blijft wat hij is.
  const { inhoud, mime } = await verkleinAlsNodig(bestand);
  if (inhoud.size > GROOTSTE_BESTAND) {
    // Alleen wat de browser niet kón verkleinen komt hier. Wél versturen zou halverwege
    // stranden en dan zegt de browser "geen verbinding" — een raadsel in plaats van een
    // antwoord.
    const mb = Math.round(inhoud.size / 1024 / 1024);
    return { fout: `Dit bestand is te groot (${mb} MB); de grens ligt op ${Math.round(GROOTSTE_BESTAND / 1024 / 1024)} MB.` };
  }
  try {
    const antwoord = await fetch(`/api/bestanden?naam=${encodeURIComponent(bestand.name)}`, {
      method: "POST",
      body: inhoud,
      headers: { "content-type": mime || "application/octet-stream" },
      redirect: "manual"
    });
    // `manual`: een 302 naar Authentik komt hier aan als een ondoorzichtig antwoord.
    if (antwoord.type === "opaqueredirect") return { fout: "Je sessie is verlopen. Meld je opnieuw aan en probeer het nog eens." };
    if (antwoord.status === 413) return { fout: "Dit bestand is te groot voor een notitie." };
    if (antwoord.status === 415) {
      // De server schrijft de reden in het Nederlands: "groter dan 20 MB", "leeg bestand".
      const uitleg = await antwoord.json().catch(() => null);
      const reden = typeof uitleg?.fout === "string" ? uitleg.fout : "de app kan dit bestand niet lezen";
      return { fout: `Het bestand is niet opgeslagen — ${reden}.` };
    }
    if (!antwoord.ok) return { fout: "Het bestand kon niet worden opgeslagen." };
    const gegevens = await antwoord.json();
    return {
      url: gegevens.url as string,
      afbeelding: Boolean(gegevens.afbeelding),
      naam: (gegevens.bestandsnaam as string) || bestand.name,
      grootte: Number(gegevens.grootte ?? inhoud.size),
      mime: (gegevens.mime as string) || mime
    };
  } catch {
    // Bestanden gaan niet door de wachtrij (die bewaart tekst), dus offline lukt dit niet.
    return { fout: "Geen verbinding — een bestand kan pas online mee." };
  }
}

/**
 * Waar een afbeelding terechtkomt.
 *
 * Niet zomaar "op de selectie": na het invoegen staat de selectie óp die afbeelding (een
 * knoopselectie), en dan vervángt de volgende invoeging hem. Koos je er drie tegelijk, dan
 * hield je er één over — de laatste.
 */
function waarDeAfbeeldingHoort(editor: TipTapEditor): number | { from: number; to: number } {
  const staat = editor.state;

  // Nooit in de tekst getikt (je tikt meteen op het fototoetsje)? Dan achteraan, want
  // bovenaan de notitie heeft een foto niets te zoeken. De editor houdt onderaan altijd een
  // lege regel klaar; die neemt de afbeelding over. Anders schuift die regel telkens mee naar
  // beneden en staat er tussen elke twee foto's een witregel.
  if (!editor.isFocused) {
    const einde = staat.doc.content.size;
    const laatste = staat.doc.lastChild;
    const legeSlotregel = laatste?.type.name === "paragraph" && laatste.content.size === 0;
    return legeSlotregel ? { from: einde - laatste!.nodeSize, to: einde } : einde;
  }

  // Staat de cursor op een lege regel, dan geldt hetzelfde: die regel gaat op in de foto.
  const plek = staat.doc.resolve(staat.selection.to);
  const alinea = plek.parent;
  if (plek.depth > 0 && alinea.type.name === "paragraph" && alinea.content.size === 0) {
    return { from: plek.before(), to: plek.after() };
  }
  return staat.selection.to;
}

/**
 * Ziet dit eruit als markdown? Alleen de vormen die je niet per ongeluk typt: een kop, een
 * lijstje, een codehek of een tabelrij aan het begin van een regel. Een zin met een sterretje
 * erin blijft gewoon een zin.
 */
function lijktOpMarkdown(tekst: string): boolean {
  if (tekst.trim().length === 0) return false;
  return /^(#{1,6} |[-*+] |\d+[.)] |```|> |\|.*\|\s*$)/m.test(tekst);
}

/** Zoekt de term in de zichtbare tekst en markeert hem, zonder de inhoud aan te raken. */
function markeer(blad: HTMLElement | null, term: string): void {
  const kan = typeof CSS !== "undefined" && "highlights" in CSS;
  if (!kan) return;
  CSS.highlights.delete("treffer");
  if (!blad || term.trim().length < 2) return;

  const zoek = term.trim().toLowerCase();
  const reeks: Range[] = [];
  const loper = document.createTreeWalker(blad, NodeFilter.SHOW_TEXT);
  let knoop = loper.nextNode();
  while (knoop) {
    const tekst = (knoop.nodeValue ?? "").toLowerCase();
    let vanaf = tekst.indexOf(zoek);
    while (vanaf !== -1) {
      const bereik = document.createRange();
      bereik.setStart(knoop, vanaf);
      bereik.setEnd(knoop, vanaf + zoek.length);
      reeks.push(bereik);
      vanaf = tekst.indexOf(zoek, vanaf + zoek.length);
    }
    knoop = loper.nextNode();
  }
  if (reeks.length > 0) {
    CSS.highlights.set("treffer", new Highlight(...reeks));
    // Meteen naar de eerste treffer: dat is de belofte "opent op de eerste match".
    reeks[0].startContainer.parentElement?.scrollIntoView({ block: "center", behavior: "smooth" });
  }
}

type Props = {
  notitieId: string;
  inhoud: Blok | null;
  zoekterm?: string;
  /** `[[` in de tekst: de app opent de notitiekiezer en voegt de link daarna zelf in. */
  opLinkZoeken?: () => void;
  opNotitieOpenen?: (id: string) => void;
  /** De app houdt de editor vast om er later iets in te kunnen zetten (een link). */
  opEditor?: (editor: TipTapEditor | null) => void;
  /** Iets ging mis met een afbeelding: de app laat het onderin zien. */
  opMelding?: (tekst: string) => void;
  // De id gaat mee met elke wijziging. Zonder dat schreef het wegschrijven bij het wisselen
  // van notitie de óúde inhoud weg onder het níeuwe id — dat kostte bij de eerste proef op
  // de laptop bijna een notitie.
  onWijzig: (inhoud: Blok, notitieId: string) => void;
};

export function Editor({ notitieId, inhoud, zoekterm, opLinkZoeken, opNotitieOpenen, opEditor, opMelding, onWijzig }: Props) {
  const wachtRef = useRef<number | null>(null);
  const idRef = useRef(notitieId);
  const bladRef = useRef<HTMLDivElement | null>(null);
  // Hetzelfde blad, maar dan als state: de sleepgreep moet weten wannéér het er is, en een ref
  // geeft geen seintje als hij gevuld wordt.
  const [blad, setBlad] = useState<HTMLDivElement | null>(null);
  const [zoekInNotitie, setZoekInNotitie] = useState("");
  const [zoekOpen, setZoekOpen] = useState(false);
  // De notitie als platte markdown. `null` betekent: gewoon de editor.
  const [bron, setBron] = useState<string | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer" } },
        // Het codeblok komt hieronder apart: dat van ons tekent een mermaid-diagram onder de
        // code zodra je `mermaid` als taal opgeeft.
        codeBlock: false
      }),
      Codeblok.configure({ languageClassPrefix: "taal-" }),
      TaskList,
      // Genest afvinken, zoals in Keep en Apple Notes: een subtaak onder een taak.
      TaskItem.configure({ nested: true }),
      Image.configure({ inline: false, allowBase64: false }),
      Bijlage,
      Callout,
      // Tabellen zonder sleepbare kolombreedtes: op een telefoon is dat handvat niet te raken
      // en op een breed scherm levert het vooral per ongeluk versleepte kolommen op.
      TableKit.configure({ table: { resizable: false } }),
      NotitieLink.configure({ opZoeken: () => opLinkZoeken?.() }),
      Vouwen,
      Placeholder.configure({ placeholder: "Begin hier te schrijven…" })
    ],
    content: inhoud ?? LEEG,
    editorProps: {
      attributes: { class: "tiptap", spellcheck: "true" },
      // `handleClick` en niet `handleClickOn`: die laatste bleek bij een inline-knoop in de
      // proef niet af te gaan, waardoor een klik op een link niets deed.
      handleClick(kijk, pos, gebeurtenis) {
        // De kop van een uitgelicht blok is een knop: hij wisselt tussen info, tip en let op.
        // Zo hoeft er voor elke soort geen eigen knopje in de werkbalk te staan.
        if ((gebeurtenis.target as HTMLElement)?.closest?.(".calloutkop")) {
          const plek = kijk.state.doc.resolve(Math.min(Math.max(pos, 0), kijk.state.doc.content.size));
          for (let diepte = plek.depth; diepte >= 0; diepte--) {
            const knoop = plek.node(diepte);
            if (knoop.type.name === "callout") {
              const soorten = CALLOUT_SOORTEN.map((s) => s.soort);
              const nu = String(knoop.attrs.soort ?? "info");
              const volgende = soorten[(soorten.indexOf(nu as never) + 1) % soorten.length];
              kijk.dispatch(kijk.state.tr.setNodeMarkup(plek.before(diepte), undefined, { ...knoop.attrs, soort: volgende }));
              return true;
            }
          }
        }

        const doel = (gebeurtenis.target as HTMLElement)?.closest?.("[data-notitie]");
        const id = doel?.getAttribute("data-notitie");
        if (id && opNotitieOpenen) {
          opNotitieOpenen(id);
          return true;
        }
        return false;
      },
      handlePaste(kijk, gebeurtenis) {
        // Elk bestand, niet alleen afbeeldingen: een pdf uit je downloads plakken hoort ook
        // te werken. Wat we niet kunnen tonen wordt een bijlage om te openen.
        const bestanden = Array.from(gebeurtenis.clipboardData?.files ?? []);
        if (bestanden.length > 0) {
          gebeurtenis.preventDefault();
          void plaatsBestanden(kijk, bestanden);
          return true;
        }

        // Markdown uit een ander programma: koppen, lijstjes en tabellen komen er anders als
        // één grijze lap tekst in, inclusief de hekjes en streepjes. Alleen bij platte tekst;
        // brengt de bron opmaak mee (html), dan weet TipTap zelf beter wat het is.
        const plat = gebeurtenis.clipboardData?.getData("text/plain") ?? "";
        const opgemaakt = gebeurtenis.clipboardData?.getData("text/html") ?? "";
        if (!opgemaakt && lijktOpMarkdown(plat)) {
          gebeurtenis.preventDefault();
          editor?.chain().focus().insertContent(vanMarkdown(plat).content ?? []).run();
          return true;
        }
        return false;
      },
      handleDrop(kijk, gebeurtenis) {
        const bestanden = Array.from((gebeurtenis as DragEvent).dataTransfer?.files ?? []);
        if (bestanden.length === 0) return false;
        gebeurtenis.preventDefault();
        void plaatsBestanden(kijk, bestanden);
        return true;
      }
    },
    onUpdate({ editor }) {
      // Niet bij elke toetsaanslag naar de wachtrij: dat zou honderden berichten per minuut
      // opleveren. Zeven tienden van een seconde stil is "klaar met typen".
      if (wachtRef.current) window.clearTimeout(wachtRef.current);
      const vanNotitie = idRef.current;
      wachtRef.current = window.setTimeout(() => onWijzig(editor.getJSON() as Blok, vanNotitie), 700);
    }
  });

  // Het blok waar de muis boven hangt; daar komt de sleepgreep naast te staan.
  const zwevend = useZwevendBlok(editor, bron === null ? blad : null);

  /**
   * Tikken op de lege ruimte onder de tekst: de cursor gaat naar het eind en het toetsenbord
   * komt op. `mousedown` en niet `pointerdown`: een veegbeweging om te scrollen levert op de
   * telefoon geen mousedown op, dus zo pakken we alleen echte tikken.
   */
  function opLegeRuimte(gebeurtenis: React.MouseEvent<HTMLDivElement>) {
    if (!editor) return;
    const doel = gebeurtenis.target as HTMLElement;
    const opDeAchtergrond =
      doel === bladRef.current || doel.classList.contains("editorbinnen") || doel.classList.contains("tiptapvak");
    // Een klik ín de tekst laat ProseMirror zelf afhandelen; die weet beter waar de cursor hoort.
    if (!opDeAchtergrond && !(doel.classList.contains("tiptap") && gebeurtenis.clientY > onderkantVanDeTekst(doel))) return;

    gebeurtenis.preventDefault();
    // Achter een alinea of een kop kun je gewoon verder schrijven. Eindigt de notitie op een
    // codeblok of een afbeelding, dan komt er eerst een lege alinea bij — anders belandt de
    // cursor ín dat codeblok en typ je per ongeluk code. Alleen dán, want een alinea erbij
    // zetten bij elke tik laat lege regels achter in de notitie.
    const laatste = editor.state.doc.lastChild;
    const kunJeDaarSchrijven = !!laatste && laatste.isTextblock && laatste.type.name !== "codeBlock";
    if (kunJeDaarSchrijven) {
      editor.commands.focus("end");
    } else {
      editor.chain().insertContentAt(editor.state.doc.content.size, { type: "paragraph" }).focus("end").run();
    }
  }

  function onderkantVanDeTekst(tiptap: HTMLElement): number {
    const laatsteBlok = tiptap.lastElementChild;
    return laatsteBlok ? laatsteBlok.getBoundingClientRect().bottom : 0;
  }

  async function voegBestandToe(bestand: File) {
    const uitkomst = await stuurBestand(bestand);
    if ("fout" in uitkomst) {
      opMelding?.(uitkomst.fout);
      return;
    }
    if (!editor) return;

    // Kan de app hem tonen, dan als afbeelding; de rest als bijlage waar je op klikt.
    const blok = uitkomst.afbeelding
      ? { type: "image", attrs: { src: uitkomst.url, alt: uitkomst.naam } }
      : {
          type: "bijlage",
          attrs: { url: uitkomst.url, naam: uitkomst.naam, grootte: uitkomst.grootte, mime: uitkomst.mime }
        };

    editor.chain().focus().insertContentAt(waarDeAfbeeldingHoort(editor), blok).run();
  }

  /**
   * Heen en weer tussen de editor en de markdown eronder. Handig om even een stuk tekst uit
   * een ander programma erin te plakken, of om te zien wat er straks in de export staat —
   * dat is dezelfde omzetting, dus wat je hier ziet is wat je krijgt.
   */
  function wisselBron() {
    if (!editor) return;
    if (bron === null) {
      setBron(naarMarkdown(editor.getJSON() as Blok));
      return;
    }
    editor.commands.setContent(vanMarkdown(bron) as Blok);
    setBron(null);
  }

  async function plaatsBestanden(_kijk: unknown, bestanden: File[]) {
    // Eén voor één, zodat ze in de notitie staan in de volgorde waarin je ze koos.
    for (const bestand of bestanden) await voegBestandToe(bestand);
  }

  // Een andere notitie openen: inhoud vervangen zonder dat "ongedaan maken" over de grens
  // van twee notities heen werkt.
  useEffect(() => {
    if (!editor) return;
    idRef.current = notitieId;
    editor.commands.setContent(inhoud ?? LEEG, { emitUpdate: false });
    CSS?.highlights?.delete("treffer");
    // Opgevouwen koppen horen bij de notitie die je net dichtdeed; een nieuwe notitie begint
    // open. De vouwstand hangt aan plekken in de tekst, en die betekenen in een ander document
    // niets meer.
    (editor.commands as any).vouwAllesOpen?.();

    // Een lege notitie is er meestal net een: dan hoort de cursor er meteen in te staan,
    // anders moet je na "nieuwe notitie" eerst nog een keer tikken voor je kunt schrijven.
    // Eén tik later dan nu: op dit moment hangt het schrijfvlak nog niet in de pagina en
    // doet `focus()` niets.
    if (editor.state.doc.textContent !== "") return;
    const straks = window.setTimeout(() => editor.commands.focus("end"), 0);
    return () => window.clearTimeout(straks);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notitieId, editor]);

  // Wat er is blijven staan bij het sluiten van een notitie hoort nog weggeschreven te
  // worden; anders verliest een snelle klik de laatste zin.
  useEffect(() => {
    // `notitieId` is hier de notitie die we verláten: dit draait vóór de inhoud wordt
    // vervangen. Wat nog in de wachtstand stond, hoort dus bij díe notitie.
    const verlaten = notitieId;
    return () => {
      if (wachtRef.current) {
        window.clearTimeout(wachtRef.current);
        wachtRef.current = null;
        if (editor) onWijzig(editor.getJSON() as Blok, verlaten);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notitieId]);

  // De app bewaart de editor zelf, zodat hij er later een link in kan zetten.
  useEffect(() => {
    opEditor?.(editor);
    return () => opEditor?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    if (zoekterm) {
      setZoekOpen(true);
      setZoekInNotitie(zoekterm);
    }
  }, [zoekterm, notitieId]);

  useEffect(() => {
    // Eerst alles openklappen: een treffer die onder een dichtgeklapte kop zit zou anders
    // gemarkeerd worden in tekst die je niet ziet, en dan lijkt het of hij niets vindt.
    if (zoekOpen && zoekInNotitie.trim().length > 1) (editor?.commands as any)?.vouwAllesOpen?.();
    markeer(bladRef.current, zoekOpen ? zoekInNotitie : "");
  }, [zoekInNotitie, zoekOpen, notitieId, inhoud, editor]);

  useEffect(() => {
    function opToets(gebeurtenis: KeyboardEvent) {
      if ((gebeurtenis.ctrlKey || gebeurtenis.metaKey) && gebeurtenis.key === "f") {
        gebeurtenis.preventDefault();
        setZoekOpen(true);
      }
      if (gebeurtenis.key === "Escape" && zoekOpen) setZoekOpen(false);
    }
    addEventListener("keydown", opToets);
    return () => removeEventListener("keydown", opToets);
  }, [zoekOpen]);

  if (!editor) return <div className="editorblad" />;

  return (
    <>
      {zoekOpen && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 20px", borderBottom: "1px solid var(--lijn)" }}>
          <Icoon naam="zoek" grootte={15} />
          <input
            autoFocus
            value={zoekInNotitie}
            onChange={(e) => setZoekInNotitie(e.target.value)}
            placeholder="Zoeken in deze notitie"
            style={{ flexGrow: 1, border: 0, outline: "none", background: "transparent" }}
          />
          <button className="knop stil" onClick={() => setZoekOpen(false)} aria-label="Zoeken sluiten">
            <Icoon naam="kruis" grootte={15} />
          </button>
        </div>
      )}

      {/* Het hele blad is schrijfgebied: tik je onder de laatste regel, dan zet de cursor
          zich aan het eind en gaat het toetsenbord open. Zonder dit is alleen de tekst zelf
          aanraakbaar en gebeurt er niets als je in de lege ruimte tikt. */}
      {bron === null ? (
        <div
          className="editorblad"
          ref={(element) => {
            bladRef.current = element;
            setBlad(element);
          }}
          onMouseDown={opLegeRuimte}
        >
          <div className="editorbinnen">
            <EditorContent editor={editor} className="tiptapvak" />
          </div>
          {zwevend && <Blokgreep editor={editor} blok={zwevend} opMelding={opMelding} />}
        </div>
      ) : (
        <div className="editorblad">
          <div className="editorbinnen">
            <textarea
              className="bronvak"
              autoFocus
              spellCheck={false}
              value={bron}
              onChange={(e) => setBron(e.target.value)}
              aria-label="De notitie als markdown"
            />
            <p style={{ fontSize: 12, color: "var(--gedempt)", margin: "8px 0 0" }}>
              Dit is precies wat er in de export komt te staan. Klik nog eens op{" "}
              <strong>&lt;/&gt;</strong> om het terug te zetten in de editor.
            </p>
          </div>
        </div>
      )}

      <Werkbalk
        editor={editor}
        opLinkZoeken={opLinkZoeken}
        opBestand={voegBestandToe}
        bronAan={bron !== null}
        wisselBron={wisselBron}
      />
    </>
  );
}

function Werkbalk({
  editor,
  opLinkZoeken,
  opBestand,
  bronAan,
  wisselBron
}: {
  editor: TipTapEditor;
  opLinkZoeken?: () => void;
  opBestand: (bestand: File) => Promise<void>;
  bronAan: boolean;
  wisselBron: () => void;
}) {
  /** Een bestandskiezer openen en alles wat je kiest in de notitie zetten. */
  const kies = (soorten: string) => {
    const kiezer = document.createElement("input");
    kiezer.type = "file";
    // `image/*` en niet een lijstje met types: op de telefoon verstopt zo'n lijstje de helft
    // van je foto's. Voor bijlagen filteren we helemaal niet.
    if (soorten) kiezer.accept = soorten;
    kiezer.multiple = true;
    kiezer.onchange = async () => {
      for (const bestand of Array.from(kiezer.files ?? [])) await opBestand(bestand);
    };
    kiezer.click();
  };

  const knop = (naam: IcoonNaam, titel: string, actief: boolean, doe: () => void) => (
    <button
      className={`knop stil${actief ? " actief" : ""}`}
      title={titel}
      aria-label={titel}
      aria-pressed={actief}
      onMouseDown={(e) => e.preventDefault()}
      onClick={doe}
    >
      <Icoon naam={naam} grootte={18} />
    </button>
  );

  const bronKnop = (
    <button
      className={`knop stil${bronAan ? " actief" : ""}`}
      title={bronAan ? "Terug naar de editor" : "De notitie als markdown bewerken"}
      aria-label="Bron-modus"
      aria-pressed={bronAan}
      onMouseDown={(e) => e.preventDefault()}
      onClick={wisselBron}
    >
      <span style={{ fontWeight: 600, fontSize: 13, fontFamily: "var(--schrift-code)" }}>&lt;/&gt;</span>
    </button>
  );

  // In de bron-modus slaat de rest van de werkbalk nergens op: je typt daar gewone tekst.
  if (bronAan) return <div className="werkbalk">{bronKnop}</div>;

  return (
    <div className="werkbalk">
      <button
        className={`knop stil${editor.isActive("heading", { level: 2 }) ? " actief" : ""}`}
        title="Kop"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <span style={{ fontWeight: 600, fontSize: 15 }}>Aa</span>
      </button>
      {knop("lijstje", "Checklist", editor.isActive("taskList"), () => editor.chain().focus().toggleTaskList().run())}
      {knop("notitie", "Opsomming", editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run())}
      {knop("meer", "Codeblok", editor.isActive("codeBlock"), () => editor.chain().focus().toggleCodeBlock().run())}
      {knop("link", "Link naar een andere notitie ([[)", false, () => opLinkZoeken?.())}
      {knop("uitroep", "Uitgelicht blok (klik op de kop om te wisselen)", editor.isActive("callout"), () => {
        if (editor.isActive("callout")) editor.chain().focus().lift("callout").run();
        else editor.chain().focus().wrapIn("callout", { soort: "waarschuwing" }).run();
      })}
      {knop("tabel", "Tabel invoegen", editor.isActive("table"), () => {
        if (editor.isActive("table")) editor.chain().focus().deleteTable().run();
        else editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
      })}
      {knop("foto", "Afbeelding invoegen", false, () => kies("image/*"))}
      {knop("paperclip", "Bijlage toevoegen (elk bestand)", false, () => kies(""))}
      {bronKnop}
    </div>
  );
}
